import { ICreateActivity, NewActivityStatus } from 'common/interfaces/activity';
import { IUser, UserType } from 'common/interfaces/user';
import { IconButton } from 'components/action';
import { ContentEditable } from 'components/content';
import { DropdownContainer, OptionsList } from 'components/list';
import { computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { IAppOnlyStore } from 'stores/AllStores';
import {
    LoadingFullScreen,
    SavingIndicator,
    SavingIndicatorStatus,
} from 'components/indicator';
import {
    DatePickerDropdown,
    SelectTime,
    ITimeValue,
    RemindTime,
    IRemindTimeValue,
} from 'components/calendar';

// Styles
const s = require('./ActivityCreateOrEdit.css');
const addIcon = require('assets/plus icon.svg');

// Props
interface IActivityCreateOrEditProps extends IAppOnlyStore {
    activityId?: number | null;
}

// Component
@inject('store')
@observer
export class ActivityCreateOrEdit extends React.Component<
    IActivityCreateOrEditProps,
    {}
> {
    static get defaultProps(): IActivityCreateOrEditProps {
        return {
            activityId: null,
        };
    }

    constructor(props: IActivityCreateOrEditProps) {
        super(props);

        if (this.props.activityId === null) {
            this.props.store.activitiesCreateOrEdit.initializeNewActivity(
                this.props.store.lead.openLeadId
            );
        }
    }

    @observable private allFieldsAreRequiredError: boolean = false;

    @computed private get loadingOptions(): boolean {
        return (
            this.props.store.activities.loadingAllActivityTypes ||
            this.props.store.users.loadingAll
        );
    }

    @computed private get openLeadSalesRepIds(): number[] {
        return this.props.store.lead.openLeadDetails.salesReps.map(
            rep => rep.id
        );
    }

    @computed private get usersOptions(): Array<{ value: any; label: string }> {
        const { user, users } = this.props.store;

        return users.allActiveSalesUsers
            .filter(
                activeUser =>
                    activeUser.id === user.id ||
                    activeUser.type === UserType.SalesManager ||
                    this.openLeadSalesRepIds.indexOf(activeUser.id) !== -1
            )
            .map(filteredUser => ({
                value: filteredUser.id,
                label: filteredUser.name,
            }));
    }

    @computed private get activityToEdit(): ICreateActivity | null {
        const { activitiesCreateOrEdit } = this.props.store;

        if (this.props.activityId !== null) {
            const indexOfActivity = activitiesCreateOrEdit.editingActivitiesIds.indexOf(
                this.props.activityId
            );
            return indexOfActivity !== -1
                ? activitiesCreateOrEdit.editingActivities[indexOfActivity]
                : null;
        } else {
            return activitiesCreateOrEdit.newActivity;
        }
    }

    @computed private get isReadyToBeSaved(): boolean {
        const {
            typeId,
            date,
            assignedToUserId,
            descriptionTooLong,
        } = this.activityToEdit;

        if (descriptionTooLong) {
            return false;
        }

        return this.activityToEdit !== null
            ? typeId !== null && date !== null && assignedToUserId !== null
            : false;
    }

    public render() {
        const {
            activities,
            leadActivities,
            activitiesCreateOrEdit,
            users,
            user,
        } = this.props.store;

        return (
            <div className={s.define}>
                <div className={s.row}>
                    <div className={s.col}>
                        <p>Activity</p>
                        <DropdownContainer
                            ref={this.referenceActivityTypeDropdown}
                            value={
                                this.activityToEdit !== null
                                    ? this.activityToEdit.typeName
                                    : null
                            }
                            label={
                                this.activityToEdit !== null
                                    ? this.activityToEdit.typeName === null
                                        ? 'Activity type'
                                        : null
                                    : null
                            }
                        >
                            <OptionsList
                                label="Activities"
                                onChange={this.handleActivityTypeChange}
                                value={
                                    this.activityToEdit !== null
                                        ? this.activityToEdit.typeId
                                        : null
                                }
                                options={
                                    this.activityToEdit !== null
                                        ? activities.allActivityTypes
                                        : []
                                }
                            />
                        </DropdownContainer>
                    </div>

                    <div className={s.col}>
                        <p>Due by</p>
                        <DatePickerDropdown
                            onChange={this.handleActivityDateChange}
                            valueCanBeCleared={true}
                            value={
                                this.activityToEdit !== null
                                    ? this.activityToEdit.date
                                    : null
                            }
                            label={
                                this.activityToEdit !== null
                                    ? this.activityToEdit.date === null
                                        ? 'Date'
                                        : null
                                    : null
                            }
                        />
                    </div>

                    {this.activityToEdit !== null &&
                        this.activityToEdit.date !== null && (
                            <div className={s.col}>
                                <p>Due at</p>
                                <SelectTime
                                    onChange={this.handleActivityTimeChange}
                                    value={
                                        this.activityToEdit !== null
                                            ? this.activityToEdit.time.value
                                            : null
                                    }
                                    label={
                                        this.activityToEdit !== null
                                            ? this.activityToEdit.time.value ===
                                              null
                                                ? 'Time'
                                                : null
                                            : null
                                    }
                                />
                            </div>
                        )}

                    {this.activityToEdit !== null &&
                        this.activityToEdit.date !== null && (
                            <div className={s.col}>
                                <p>Remind</p>
                                <RemindTime
                                    onChange={
                                        this.handleActivityRemindAtTimeChange
                                    }
                                    value={
                                        typeof this.activityToEdit.remindAt !==
                                        'undefined'
                                            ? this.activityToEdit.remindAt.value
                                            : undefined
                                    }
                                    label={
                                        typeof this.activityToEdit.remindAt !==
                                        'undefined'
                                            ? this.activityToEdit.remindAt.label
                                            : undefined
                                    }
                                />
                            </div>
                        )}

                    <div className={s.col}>
                        <p>Assign to</p>
                        <DropdownContainer
                            ref={this.referenceActivityAssignedUserDropdown}
                            value={
                                this.activityToEdit !== null
                                    ? this.activityToEdit.assignedToUserName
                                    : null
                            }
                            label={
                                this.activityToEdit !== null
                                    ? this.activityToEdit.assignedToUserName ===
                                      null
                                        ? 'Pick person'
                                        : null
                                    : null
                            }
                        >
                            <OptionsList
                                label="People"
                                onChange={this.handleActivityAssignedUserChange}
                                value={[
                                    {
                                        user: user.id,
                                        value: user.name,
                                    },
                                ]}
                                options={this.usersOptions}
                            />
                        </DropdownContainer>
                    </div>
                </div>

                <div className={s.details}>
                    <ContentEditable
                        className={s.descriptionContainer}
                        classNameForField={s.description}
                        contentEditable={true}
                        charactersLimit={50}
                        html={
                            this.activityToEdit !== null
                                ? this.activityToEdit.descriptionHtml
                                : ''
                        }
                        placeholder="Activity description..."
                        onChange={this.handleDescriptionChange}
                    />

                    <IconButton
                        className={s.saveButton}
                        label={
                            this.props.activityId === null
                                ? 'Create activity'
                                : 'Save changes'
                        }
                        labelOnRight={true}
                        showLabel={true}
                        icon={addIcon}
                        onClick={this.handleActivitySave}
                    />
                </div>

                <SavingIndicator
                    className={s.savingIndicator}
                    labels={{
                        saving: 'Saving activity...',
                        error: this.activityToEdit.descriptionTooLong
                            ? 'Shorten activity description'
                            : this.allFieldsAreRequiredError
                            ? 'Activity type, due date and assigned user are required'
                            : 'Could not save, try again',
                        success: 'Activity saved!',
                    }}
                    status={
                        this.activityToEdit !== null
                            ? this.activityToEdit.status ===
                              NewActivityStatus.Saving
                                ? SavingIndicatorStatus.Saving
                                : this.activityToEdit.status ===
                                      NewActivityStatus.Error ||
                                  this.allFieldsAreRequiredError ||
                                  this.activityToEdit.descriptionTooLong
                                ? SavingIndicatorStatus.Error
                                : this.activityToEdit.status ===
                                  NewActivityStatus.Success
                                ? SavingIndicatorStatus.Success
                                : SavingIndicatorStatus.None
                            : null
                    }
                />

                <LoadingFullScreen
                    fitInContainer={true}
                    active={this.loadingOptions}
                />
            </div>
        );
    }

    private activityTypeDropdown: DropdownContainer = null;
    private referenceActivityTypeDropdown = (ref: DropdownContainer) =>
        (this.activityTypeDropdown = ref);

    private activityAssignedUserDropdown: DropdownContainer = null;
    private referenceActivityAssignedUserDropdown = (ref: DropdownContainer) =>
        (this.activityAssignedUserDropdown = ref);

    private handleActivityTypeChange = (changed: {
        value: number;
        label: string;
    }) => {
        this.activityToEdit.typeId = changed.value;
        this.activityToEdit.typeName = changed.label;
        this.allFieldsAreRequiredError = false;

        if (this.activityTypeDropdown) {
            this.activityTypeDropdown.closeDropdown();
        }
    };

    private handleActivityDateChange = (date: moment.Moment) => {
        const updateTimeToo = this.activityToEdit.date === null;

        if (updateTimeToo) {
            date = date.hours(12);
        }

        this.activityToEdit.date = date;
        this.allFieldsAreRequiredError = false;

        if (updateTimeToo) {
            this.activityToEdit.time.value = {
                hours: date.hours(),
                minutes: date.minutes(),
                totalMinutes: date.hours() * 60 + date.minutes(),
            };
        }
    };

    private handleActivityTimeChange = (time: {
        value: ITimeValue;
        label: string;
    }) => {
        this.activityToEdit.time.value = time.value;
        this.activityToEdit.time.label = time.label;
        this.allFieldsAreRequiredError = false;
    };

    private handleActivityRemindAtTimeChange = (time: {
        value: IRemindTimeValue;
        label: string;
    }) => {
        this.activityToEdit.remindAt.value = time.value;
        this.activityToEdit.remindAt.label = time.label;
        this.allFieldsAreRequiredError = false;
    };

    private handleActivityAssignedUserChange = (changed: {
        value: number;
        label: string;
    }) => {
        this.activityToEdit.assignedToUserId = changed.value;
        this.activityToEdit.assignedToUserName = changed.label;
        this.allFieldsAreRequiredError = false;

        if (this.activityAssignedUserDropdown) {
            this.activityAssignedUserDropdown.closeDropdown();
        }
    };

    private handleDescriptionChange = (
        html: string,
        text: string,
        isOverCharacterLimit: boolean
    ) => {
        this.activityToEdit.descriptionHtml = html;
        this.activityToEdit.descriptionText = text;
        this.activityToEdit.descriptionTooLong = isOverCharacterLimit;
        this.allFieldsAreRequiredError = false;
    };

    private handleActivitySave = () => {
        if (this.isReadyToBeSaved) {
            this.allFieldsAreRequiredError = false;

            if (this.props.activityId === null) {
                this.props.store.activitiesCreateOrEdit.createActivity();
            } else {
                this.props.store.activitiesCreateOrEdit.editActivity(
                    this.props.activityId
                );
            }
        } else {
            if (this.activityToEdit.descriptionTooLong === false) {
                this.allFieldsAreRequiredError = true;
            }
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/activity/ActivityCreateOrEdit.tsx